/**
 * audience-voice.ts
 * ─────────────────────────────────────────────────────────────────
 * Contenu centralisé par profil utilisateur.
 *
 * Chaque audience a sa propre voix, son propre ton, ses propres mots.
 * Pas de jargon, pas de formules génériques, pas de listes interminables.
 * Un seul objectif : que le lecteur se dise « ils me comprennent ».
 *
 * Pour modifier le wording d'une audience, c'est ICI que ça se passe.
 */

import type { AudienceId } from '@shared/constants/audiences';

// ════════════════════════════════════════════════════════════════
// TYPES
// ════════════════════════════════════════════════════════════════

export type AudienceTone =
  | 'warm-pedagogic' // particuliers : simple, rassurant
  | 'pragmatic-roi' // pros : direct, sans blabla
  | 'strategic' // entreprises : posé, fiable
  | 'tertiary-compliance' // tertiaire : obligation, solution
  | 'industrial-engineering' // industrie : technique mais clair
  | 'institutional' // collectivités : rigoureux, public
  | 'collective' // copropriétés : collectif, AG
  | 'partner-business' // artisans : partenariat, confiance
  | 'farming-roi'; // agriculteurs : concret, exploitation

export interface AudienceCta {
  label: string;
  href: string;
  iconName?: string;
}

export interface AudienceProofPoint {
  iconName: string;
  label: string;
  sub: string;
}

export interface AudienceFaqItem {
  q: string;
  a: string;
}

export interface AudienceVoice {
  id: AudienceId;
  tone: AudienceTone;

  hero: {
    eyebrow: string;
    promise: string;
    headline: string;
    headlineAccent: string;
    subheadline: string;
    bullets: string[];
    primaryCta: AudienceCta;
    secondaryCta: AudienceCta;
    microPromise: string;
  };

  proof: {
    eyebrow: string;
    title?: string;
    points: AudienceProofPoint[];
  };

  method: {
    eyebrow: string;
    title: string;
    description: string;
  };

  tools: {
    eyebrow: string;
    title: string;
    description: string;
  };

  caseStudy: {
    eyebrow: string;
    title: string;
  };

  faq: {
    eyebrow: string;
    title: string;
    items: AudienceFaqItem[];
  };

  ctaFinal: {
    badge?: string;
    title: string;
    description: string;
    primary: AudienceCta;
    secondary: AudienceCta;
    footnote?: string;
  };
}

// ════════════════════════════════════════════════════════════════
// PARTICULIERS
// ════════════════════════════════════════════════════════════════

const PARTICULIERS: AudienceVoice = {
  id: 'particuliers',
  tone: 'warm-pedagogic',
  hero: {
    eyebrow: 'Propriétaires, bailleurs, locataires',
    promise: 'On vous dit combien vous paierez vraiment, avant de signer quoi que ce soit.',
    headline: 'Un logement plus confortable,',
    headlineAccent: 'sans se ruiner.',
    subheadline:
      'Isolation, chauffage, fenêtres : quel que soit votre projet, il existe des aides pour le financer. On les cumule pour vous et on vous met en relation avec un artisan qualifié près de chez vous.',
    bullets: [
      'Aucune avance demandée',
      'Artisan RGE de votre région',
      'Devis avec aides déjà déduites',
    ],
    primaryCta: {
      label: 'Calculer mes aides',
      href: '/particuliers/calculer-mes-aides',
      iconName: 'calculator',
    },
    secondaryCta: {
      label: 'Être rappelé sous 24 h',
      href: '/contact?source=particulier',
      iconName: 'phone',
    },
    microPromise: 'Réponse immédiate, sans inscription',
  },
  proof: {
    eyebrow: "Ce qu'on fait pour vous",
    title: 'Votre rénovation, sans stress.',
    points: [
      {
        iconName: 'wallet',
        label: 'Toutes vos aides en un calcul',
        sub: "MaPrimeRénov', primes énergie, éco-PTZ, TVA réduite, aides locales. On vérifie tout ce à quoi vous avez droit et on cumule.",
      },
      {
        iconName: 'handshake',
        label: 'Un artisan de confiance',
        sub: 'On vous présente un professionnel RGE vérifié, avec assurance décennale à jour, actif près de chez vous.',
      },
      {
        iconName: 'shield-check',
        label: 'Pas de mauvaise surprise',
        sub: "Votre devis intègre les aides en direct. Vous ne payez que le reste à charge. Pas d'avance à sortir.",
      },
    ],
  },
  method: {
    eyebrow: 'Comment ça se passe',
    title: 'Du premier calcul au dernier versement, on est avec vous.',
    description:
      "Vous nous dites ce que vous voulez faire, on vous dit combien ça coûte vraiment. On vous présente un artisan, il vient chez vous, il fait le devis. On monte le dossier d'aides. Les travaux commencent. Vous touchez vos primes. Simple.",
  },
  tools: {
    eyebrow: 'Vos outils',
    title: "Tout ce qu'il faut pour piloter votre projet.",
    description:
      "Calculateur d'aides, simulateur de travaux, catalogue des équipements éligibles, exemples de rénovations réussies.",
  },
  caseStudy: {
    eyebrow: 'Un exemple concret',
    title: 'Ils ont divisé leur reste à charge par trois. Voici comment.',
  },
  faq: {
    eyebrow: 'Vos questions',
    title: 'On vous répond franchement.',
    items: [
      {
        q: "Est-ce que j'ai droit aux aides ?",
        a: 'Très probablement. Les aides à la rénovation concernent tous les propriétaires, occupants comme bailleurs. Le montant dépend de vos revenus et des travaux prévus. Notre simulateur vous donne un chiffre en 2 minutes.',
      },
      {
        q: 'Je peux vraiment cumuler plusieurs aides ?',
        a: "Oui. MaPrimeRénov', la prime énergie, l'éco-PTZ à taux zéro et la TVA à 5,5 % se cumulent dans la grande majorité des cas. Notre calculateur respecte les plafonds légaux pour vous donner le vrai reste à charge.",
      },
      {
        q: "Quand est-ce que je reçois l'argent ?",
        a: 'Les primes sont généralement versées dans les 30 à 90 jours après la fin des travaux. On vous tient au courant à chaque étape : réception du dossier, validation, virement.',
      },
      {
        q: 'Qui vient faire les travaux chez moi ?',
        a: 'Un artisan RGE de notre réseau, sélectionné selon votre localisation et les travaux à réaliser. Qualification vérifiée, assurance à jour, clients satisfaits. Vous pouvez aussi proposer votre propre artisan.',
      },
    ],
  },
  ctaFinal: {
    badge: '2 minutes, sans inscription',
    title: 'On calcule vos aides ?',
    description:
      'Décrivez votre logement et vos travaux. On vous donne un chiffre immédiatement. Aucune carte bancaire, aucun engagement.',
    primary: {
      label: 'Calculer mes aides en 2 min',
      href: '/particuliers/calculer-mes-aides',
      iconName: 'calculator',
    },
    secondary: { label: 'Voir des rénovations réussies', href: '/cas-clients' },
    footnote: 'Aucune carte bancaire, aucune inscription, aucun spam',
  },
};

// ════════════════════════════════════════════════════════════════
// PROFESSIONNELS
// ════════════════════════════════════════════════════════════════

const PROFESSIONNELS: AudienceVoice = {
  id: 'professionnels',
  tone: 'pragmatic-roi',
  hero: {
    eyebrow: 'TPE, PME, indépendants, professions libérales',
    promise: 'On vous dit combien vous pouvez économiser. Gratuitement.',
    headline: "Vos factures d'énergie,",
    headlineAccent: 'allégées sans changer vos habitudes.',
    subheadline:
      "Commerce, restaurant, cabinet, agence : on analyse ce que vous payez aujourd'hui, on compare avec ce que propose le marché, et on négocie pour vous. Si on ne trouve rien de mieux, on vous le dit aussi.",
    bullets: [
      "Courtier indépendant des fournisseurs d'énergie",
      'Audit gratuit de vos factures',
      'Aucune commission cachée',
    ],
    primaryCta: {
      label: 'Auditer mes factures',
      href: '/professionnels/comparateur-energie',
      iconName: 'file-search',
    },
    secondaryCta: {
      label: 'Estimer mes primes travaux',
      href: '/professionnels/diagnostic-cee',
      iconName: 'file-text',
    },
    microPromise: 'Diagnostic 5 min, sans engagement',
  },
  proof: {
    eyebrow: "Ce qu'on cherche sur vos factures",
    title: 'Chaque ligne de votre facture passée au crible.',
    points: [
      {
        iconName: 'trending-down',
        label: 'Puissance souscrite trop élevée',
        sub: "Beaucoup de pros paient pour une puissance qu'ils n'utilisent jamais. On détecte l'écart et on corrige.",
      },
      {
        iconName: 'git-compare-arrows',
        label: 'Offres moins chères',
        sub: 'On compare votre contrat avec toutes les offres du marché. TotalEnergies, Engie, EDF, Eni, Octopus et les autres.',
      },
      {
        iconName: 'file-text',
        label: 'Primes sur vos travaux',
        sub: 'Si vous avez fait ou prévoyez des travaux, on vérifie les primes auxquelles vous avez droit.',
      },
    ],
  },
  method: {
    eyebrow: 'Comment ça marche',
    title: 'Vous nous envoyez une facture, on vous renvoie un rapport.',
    description:
      "On regarde votre contrat actuel, on identifie ce qui peut être amélioré, on compare avec les offres du marché. Si ça vaut le coup de changer, on s'occupe de tout. Sinon, on vous le dit et on s'arrête là.",
  },
  tools: {
    eyebrow: 'Vos outils',
    title: 'Tout pour réduire vos coûts, sans y passer vos journées.',
    description:
      "Audit de factures, comparateur d'offres, estimateur de primes travaux, exemples de pros qui ont économisé.",
  },
  caseStudy: {
    eyebrow: 'Un exemple concret',
    title: 'Un restaurant qui économise 18 % sur ses factures. Sans rien changer.',
  },
  faq: {
    eyebrow: 'FAQ',
    title: "Ce qu'il faut savoir avant de se lancer.",
    items: [
      {
        q: 'Combien ça coûte ?',
        a: "Le diagnostic et l'audit de vos factures sont gratuits et sans engagement. Notre modèle est transparent : tout est écrit dans nos conditions générales avant de signer quoi que ce soit.",
      },
      {
        q: "Vous êtes vraiment indépendants des fournisseurs d'énergie ?",
        a: 'Oui, sur le courtage énergie B2B : aucun partenariat exclusif fournisseur. On compare toutes les offres accessibles à votre profil.',
      },
      {
        q: 'Et si je change de fournisseur, il y a une coupure ?',
        a: 'Non. Le changement est purement administratif. Vous gardez le même compteur, la même alimentation. Le délai légal est de 21 jours.',
      },
      {
        q: 'Si vous ne trouvez rien de mieux ?',
        a: "On vous le dit franchement, et on ne vous facture rien. Notre intérêt, c'est que vous économisiez. Sinon, ça n'a pas de sens.",
      },
    ],
  },
  ctaFinal: {
    badge: 'Audit gratuit, sans engagement',
    title: 'Combien pouvez-vous économiser cette année ?',
    description:
      'Envoyez-nous votre dernière facture. On vous renvoie un rapport chiffré avec les économies possibles. Gratuit, sans obligation de changer de fournisseur.',
    primary: {
      label: "Lancer l'audit factures",
      href: '/professionnels/comparateur-energie',
      iconName: 'file-search',
    },
    secondary: { label: 'Estimer mes primes travaux', href: '/professionnels/diagnostic-cee' },
    footnote: 'Données sécurisées, jamais revendues',
  },
};

// ════════════════════════════════════════════════════════════════
// ENTREPRISES
// ════════════════════════════════════════════════════════════════

const ENTREPRISES: AudienceVoice = {
  id: 'entreprises',
  tone: 'strategic',
  hero: {
    eyebrow: 'ETI, grands comptes, multi-sites',
    promise: 'Un interlocuteur unique pour toute votre énergie.',
    headline: 'Votre stratégie énergie,',
    headlineAccent: 'pilotée et mesurable.',
    subheadline:
      "Achats d'énergie pour tous vos sites, primes sur vos travaux, conformité réglementaire : on centralise tout. Un référent dédié, une confidentialité totale, des livrables structurés pour votre direction.",
    bullets: ['Pilotage multi-sites', 'Conformité ISO 50001', 'Confidentialité garantie'],
    primaryCta: {
      label: 'Demander une étude chiffrée',
      href: '/contact?source=entreprise',
      iconName: 'handshake',
    },
    secondaryCta: {
      label: 'Audit obligation tertiaire',
      href: '/entreprises/decret-tertiaire',
      iconName: 'gauge',
    },
    microPromise: 'Référent dédié, réponse sous 48 h ouvrées',
  },
  proof: {
    eyebrow: "Ce qu'on apporte",
    title: 'Trois piliers pour votre direction énergie.',
    points: [
      {
        iconName: 'gauge',
        label: 'Conformité réglementaire',
        sub: "Décret Tertiaire, obligations de déclaration, trajectoire de réduction : on s'occupe de tout ce qui est obligatoire.",
      },
      {
        iconName: 'factory',
        label: 'Primes sur vos travaux',
        sub: "Chaque opération d'efficacité énergétique sur vos sites peut générer des primes. On les identifie et on les valorise au prix du marché.",
      },
      {
        iconName: 'trending-down',
        label: "Achats d'énergie négociés",
        sub: 'Mise en concurrence de tous vos sites, optimisation des contrats, négociation groupée.',
      },
    ],
  },
  method: {
    eyebrow: 'Notre approche',
    title: 'On travaille avec vos équipes, pas à leur place.',
    description:
      "On intervient en complément de vos services achats, techniques et RSE. Audit complet, plan d'action chiffré, déploiement progressif. Accord de confidentialité dès le premier contact.",
  },
  tools: {
    eyebrow: 'Vos leviers',
    title: 'Des outils pensés pour une direction énergie.',
    description:
      "Audit multi-sites, courtage d'énergie, valorisation de primes, conformité réglementaire, reporting structuré.",
  },
  caseStudy: {
    eyebrow: 'Un exemple concret',
    title: 'Une ETI qui a réduit ses coûts énergie de 22 % en 18 mois.',
  },
  faq: {
    eyebrow: 'Questions fréquentes',
    title: 'Ce que les directions nous demandent.',
    items: [
      {
        q: 'Comment garantissez-vous la confidentialité ?',
        a: 'Accord de confidentialité signé au premier échange. Référent unique. Aucune donnée transmise à des tiers. Hébergement en France, conforme RGPD.',
      },
      {
        q: 'Vous intervenez sur combien de sites ?',
        a: 'Autant que nécessaire. Nous pilotons des portefeuilles de 5 à plus de 100 sites, avec un reporting consolidé pour votre direction.',
      },
      {
        q: 'Pouvez-vous présenter en comité de direction ?',
        a: 'Oui. Nous préparons les slides, les scénarios chiffrés et nous défendons les hypothèses face à votre direction financière si vous le souhaitez.',
      },
      {
        q: 'Que se passe-t-il en cas de contrôle ?',
        a: "Nous gérons l'intégralité du contrôle. Conservation des pièces pendant 9 ans, assurance responsabilité civile professionnelle, défense des dossiers.",
      },
    ],
  },
  ctaFinal: {
    badge: 'Référent dédié, confidentialité garantie',
    title: 'Construisons votre stratégie énergie.',
    description:
      'Audit complet offert, recommandations chiffrées sous 15 jours, présentation en comité de direction si vous le souhaitez. Accord de confidentialité au premier contact.',
    primary: {
      label: 'Demander une étude chiffrée',
      href: '/contact?source=entreprise',
      iconName: 'handshake',
    },
    secondary: { label: 'Voir nos références', href: '/cas-clients' },
    footnote: 'Confidentialité, Référent dédié, Livrables structurés',
  },
};

// ════════════════════════════════════════════════════════════════
// COLLECTIVITÉS
// ════════════════════════════════════════════════════════════════

const COLLECTIVITES: AudienceVoice = {
  id: 'collectivites',
  tone: 'institutional',
  hero: {
    eyebrow: 'Mairies, EPCI, intercommunalités, bailleurs sociaux',
    promise: 'Conforme au code de la commande publique.',
    headline: 'Pilotez la rénovation',
    headlineAccent: 'de votre patrimoine bâti.',
    subheadline:
      'On audite vos bâtiments, on construit votre plan de réduction énergétique et on cumule les aides publiques disponibles. Le tout dans le respect du code de la commande publique.',
    bullets: ['Référencé UGAP', 'Marchés conformes', 'Pré-étude offerte'],
    primaryCta: {
      label: 'Étudier mon patrimoine',
      href: '/contact?source=collectivite',
      iconName: 'file-search',
    },
    secondaryCta: {
      label: 'Audit Décret Tertiaire',
      href: '/collectivites/decret-tertiaire',
      iconName: 'gauge',
    },
    microPromise: 'Pré-étude gratuite, projet de délibération inclus',
  },
  proof: {
    eyebrow: 'Le cadre réglementaire',
    title: 'Toutes vos obligations, prises en charge.',
    points: [
      {
        iconName: 'gauge',
        label: 'Décret Tertiaire',
        sub: "Déclaration annuelle, trajectoire -40 % en 2030, -50 % en 2040, -60 % en 2050. On s'occupe de tout.",
      },
      {
        iconName: 'building-2',
        label: 'Décret BACS',
        sub: 'Obligation de gestion technique pour les bâtiments de plus de 290 kW. On vous accompagne.',
      },
      {
        iconName: 'piggy-bank',
        label: 'Cumul des aides publiques',
        sub: "Fonds vert, dotations de l'État, primes énergie : on active tous les leviers pour réduire votre reste à charge.",
      },
    ],
  },
  method: {
    eyebrow: 'Notre méthode',
    title: 'De la pré-étude au vote en conseil, on porte le dossier.',
    description:
      'Note de synthèse, projet de délibération, dossier de consultation, intervention en commission ou en séance. Vous décidez, on prépare tout.',
  },
  tools: {
    eyebrow: 'Vos outils',
    title: 'Des outils conformes au code de la commande publique.',
    description: 'Audit patrimoine, marchés publics, plan de réduction, cumul de financements.',
  },
  caseStudy: {
    eyebrow: 'Un exemple concret',
    title: 'Une commune qui a mobilisé 743 000 € de subventions pour ses bâtiments.',
  },
  faq: {
    eyebrow: 'FAQ',
    title: 'Vos questions, nos réponses.',
    items: [
      {
        q: "Êtes-vous référencé sur les centrales d'achat public ?",
        a: "Oui, Solution Éco France est référencée sur l'UGAP et plusieurs centrales territoriales. Nos contrats respectent le code de la commande publique.",
      },
      {
        q: 'Pouvez-vous intervenir en conseil municipal ?',
        a: 'Oui. Nous préparons la note de synthèse, le projet de délibération, et nous pouvons présenter le dossier en commission ou en séance. Gratuitement.',
      },
      {
        q: 'Quelles aides sont cumulables ?',
        a: 'Fonds vert, DETR, DSIL, FCTVA, primes énergie, aides régionales. Notre calculateur croise tous les dispositifs.',
      },
      {
        q: 'Comment gérez-vous les bâtiments classés ?',
        a: 'Nous travaillons avec des architectes du patrimoine pour concilier rénovation thermique et contraintes patrimoniales.',
      },
    ],
  },
  ctaFinal: {
    badge: 'Pré-étude offerte',
    title: 'Audit gratuit de votre patrimoine.',
    description:
      "On évalue le potentiel d'économies de vos bâtiments et on vous donne des recommandations chiffrées pour votre prochain plan d'investissement.",
    primary: {
      label: 'Demander la pré-étude',
      href: '/contact?source=collectivite',
      iconName: 'file-search',
    },
    secondary: { label: 'Cas clients publics', href: '/cas-clients' },
    footnote: 'Confidentialité, Conformité commande publique',
  },
};

// ════════════════════════════════════════════════════════════════
// COPROPRIÉTÉS
// ════════════════════════════════════════════════════════════════

const COPROPRIETES: AudienceVoice = {
  id: 'coproprietes',
  tone: 'collective',
  hero: {
    eyebrow: 'Syndics, conseils syndicaux, copropriétaires',
    promise: 'Présentation en AG offerte.',
    headline: 'Du diagnostic au vote en AG,',
    headlineAccent: 'on vous accompagne.',
    subheadline:
      "On audite l'immeuble, on chiffre les aides par lot, on prépare la présentation pour l'assemblée générale. Notre objectif : que la rénovation soit votée dès la première convocation.",
    bullets: ['Présentation AG gratuite', 'Projet de résolution rédigé', 'Simulation par lot'],
    primaryCta: {
      label: 'Préparer mon AG',
      href: '/contact?source=copropriete',
      iconName: 'clipboard-check',
    },
    secondaryCta: {
      label: 'Calculer les aides Copro',
      href: '/coproprietes/calculer-mes-aides',
      iconName: 'calculator',
    },
    microPromise: 'Note explicative, simulation par lot, vote en 1re convocation',
  },
  proof: {
    eyebrow: 'Pour réussir votre AG',
    title: "Tout ce qu'il faut pour convaincre les copropriétaires.",
    points: [
      {
        iconName: 'clipboard-list',
        label: 'Note pour la convocation',
        sub: 'Un document clair qui explique le projet, les aides, et ce que chaque copropriétaire paiera vraiment.',
      },
      {
        iconName: 'piggy-bank',
        label: 'Aides cumulées',
        sub: "MaPrimeRénov' Copropriétés, primes énergie collectives, éco-PTZ collectif : on maximise chaque enveloppe.",
      },
      {
        iconName: 'file-signature',
        label: 'Résolution prête à voter',
        sub: "Un texte juridiquement solide, directement insérable dans l'ordre du jour par le syndic.",
      },
    ],
  },
  method: {
    eyebrow: 'Notre méthode',
    title: 'On parle le langage de tous les copropriétaires.',
    description:
      "Diagnostic de l'immeuble, scénarios chiffrés par lot, simulation du reste à charge de chacun, présentation visuelle pour l'AG. L'objectif est simple : un vote positif, sans report.",
  },
  tools: {
    eyebrow: 'Vos outils',
    title: 'Tout pour préparer votre prochaine AG.',
    description:
      "Audit énergétique, calculateur d'aides Copro, modèle de présentation AG, liste des travaux éligibles.",
  },
  caseStudy: {
    eyebrow: 'Un exemple concret',
    title: "Une copropriété passée de l'étiquette F à C, votée au premier tour.",
  },
  faq: {
    eyebrow: 'FAQ',
    title: 'Les questions que les copropriétaires posent toujours.',
    items: [
      {
        q: "Vous travaillez avec quel type d'interlocuteur ?",
        a: "Avec tout le monde : syndics professionnels, conseils syndicaux, copropriétaires qui veulent lancer le projet. On s'adapte.",
      },
      {
        q: 'Qui présente en AG ?',
        a: 'Nous, si vous le souhaitez. On fournit la note explicative, le support de présentation, le projet de résolution et on répond aux questions. Gratuitement.',
      },
      {
        q: 'Comment les aides sont réparties entre copropriétaires ?',
        a: 'Les aides collectives vont au syndicat. Les aides individuelles restent attribuées à chaque copropriétaire selon sa quote-part. Notre calculateur ventile automatiquement.',
      },
      {
        q: 'Combien de temps entre le vote et le début des travaux ?',
        a: 'Généralement 3 à 6 mois : délais de recours, notification, déblocage des aides, démarrage du chantier. On vous accompagne à chaque étape.',
      },
    ],
  },
  ctaFinal: {
    badge: 'Intervention AG offerte',
    title: 'Préparez votre prochaine AG sereinement.',
    description:
      "Pré-étude gratuite, chiffrage des aides, projet de résolution. On vous accompagne jusqu'au vote, sans engagement.",
    primary: {
      label: 'Préparer mon AG',
      href: '/contact?source=copropriete',
      iconName: 'clipboard-check',
    },
    secondary: { label: 'Calculer les aides Copro', href: '/coproprietes/calculer-mes-aides' },
    footnote: 'Intervention AG offerte, projet de résolution fourni',
  },
};

// ════════════════════════════════════════════════════════════════
// ARTISANS
// ════════════════════════════════════════════════════════════════

const ARTISANS: AudienceVoice = {
  id: 'artisans',
  tone: 'partner-business',
  hero: {
    eyebrow: 'Artisans RGE',
    promise: "On monte vos dossiers de primes. Si c'est rejeté, vous ne payez rien.",
    headline: 'Valorisez vos chantiers,',
    headlineAccent: 'sans risque.',
    subheadline:
      'Vous faites votre métier, on fait le nôtre. Mandat signé en 5 minutes, dossier déposé sous 7 jours, paiement en 30 à 60 jours. Si le dossier est rejeté, vous ne perdez pas un centime.',
    bullets: ['Aucune avance à faire', 'Prix du marché garanti', 'Conformité juridique assurée'],
    primaryCta: {
      label: 'Devenir partenaire',
      href: '/devenir-partenaire/candidater?profile=artisan',
      iconName: 'hard-hat',
    },
    secondaryCta: {
      label: 'Voir comment ça marche',
      href: '/artisans#methode',
      iconName: 'eye',
    },
    microPromise: 'Mandat en 5 min, 1er dépôt sous 7 jours',
  },
  proof: {
    eyebrow: 'Ce que vous y gagnez',
    title: 'Vous faites le chantier, on porte le dossier.',
    points: [
      {
        iconName: 'piggy-bank',
        label: 'Zéro avance de trésorerie',
        sub: 'Vous ne sortez rien. On prend le risque du rejet à notre charge.',
      },
      {
        iconName: 'sparkles',
        label: 'Prix du marché garanti',
        sub: 'Vos primes sont valorisées au tarif en vigueur le jour du dépôt. Aucun frais caché.',
      },
      {
        iconName: 'shield-check',
        label: 'Cadre juridique blindé',
        sub: 'Respect du décret de mars 2022, charte qualité officielle, assurance RC Pro.',
      },
    ],
  },
  method: {
    eyebrow: 'Comment ça marche',
    title: 'Vous posez, on dépose. Aussi simple que ça.',
    description:
      "Vous nous transmettez vos pièces de chantier. On vérifie, on dépose le dossier, on suit l'instruction, on défend en cas de contrôle. Vous touchez la prime en 30 à 60 jours.",
  },
  tools: {
    eyebrow: 'Vos outils',
    title: 'Des outils pensés pour les artisans.',
    description:
      'Espace dédié, lecture automatique de vos pièces, catalogue des primes, tarifs en temps réel.',
  },
  caseStudy: {
    eyebrow: 'Un exemple concret',
    title: 'Un artisan qui a valorisé 42 800 € de primes en un trimestre.',
  },
  faq: {
    eyebrow: 'FAQ',
    title: 'Ce que vous voulez savoir avant de signer.',
    items: [
      {
        q: 'Combien de temps pour être payé ?',
        a: '30 à 60 jours en moyenne après le dépôt du dossier. Vous suivez chaque étape via votre référent dédié.',
      },
      {
        q: 'Que se passe-t-il en cas de contrôle ?',
        a: 'On gère tout. Contrôle sur place ou sur dossier, on défend votre dossier du début à la fin.',
      },
      {
        q: 'Mon RGE expire bientôt, vous me prévenez ?',
        a: 'Oui. On vérifie chaque dossier en temps réel et on vous alerte automatiquement 60 jours avant expiration.',
      },
      {
        q: 'Et si mon dossier est rejeté ?',
        a: "Vous ne payez rien. On prend le risque. Notre métier, c'est de monter des dossiers qui passent du premier coup.",
      },
    ],
  },
  ctaFinal: {
    badge: 'Mandataire CEE agréé',
    title: "Rejoignez notre réseau d'artisans partenaires.",
    description:
      'Vérification de votre qualification RGE en temps réel. Mandat en 5 minutes. Premier dossier déposé sous 7 jours. Aucun engagement de volume.',
    primary: {
      label: 'Candidater (5 min)',
      href: '/devenir-partenaire/candidater?profile=artisan',
      iconName: 'hard-hat',
    },
    secondary: { label: 'Lire nos CGV', href: '/legal/cgv-courtier-energie' },
    footnote: 'RC Pro, conformité réglementaire',
  },
};

// ════════════════════════════════════════════════════════════════
// AGRICULTEURS
// ════════════════════════════════════════════════════════════════

const AGRICULTEURS: AudienceVoice = {
  id: 'agriculteurs',
  tone: 'farming-roi',
  hero: {
    eyebrow: 'Exploitations agricoles, élevage, viticulture, céréales',
    promise: 'Diagnostic gratuit. On vous dit ce que vous pouvez vraiment toucher.',
    headline: "Vos bâtiments d'exploitation,",
    headlineAccent: 'financés par les aides.',
    subheadline:
      "Hangars, étables, serres, bâtiments d'élevage : il existe des primes pour les moderniser. On identifie celles qui s'appliquent à votre exploitation et on vous dit combien.",
    bullets: [
      'Primes pour vos travaux agricoles',
      'Aides régionales incluses',
      'Aucune avance de trésorerie',
    ],
    primaryCta: {
      label: 'Lancer le diagnostic',
      href: '/agriculteurs/diagnostic-cee',
      iconName: 'scan-line',
    },
    secondaryCta: {
      label: 'Voir les aides disponibles',
      href: '/cee/fiches?secteur=AGRI',
      iconName: 'file-text',
    },
    microPromise: 'Diagnostic 60 s, sans inscription',
  },
  proof: {
    eyebrow: 'Vos aides concrètement',
    title: 'Ce que votre exploitation peut vraiment toucher.',
    points: [
      {
        iconName: 'zap',
        label: 'Primes pour vos équipements',
        sub: "Pompes à chaleur, régulation d'élevage, éclairage LED de hangar, isolation de toiture.",
      },
      {
        iconName: 'tractor',
        label: 'PCAE et aides nationales',
        sub: 'Le plan de compétitivité de votre Région, plus les aides de FranceAgriMer.',
      },
      {
        iconName: 'sparkles',
        label: 'Photovoltaïque',
        sub: 'Ombrières, toiture de hangar, autoconsommation ou revente. Cumulable avec les primes.',
      },
    ],
  },
  method: {
    eyebrow: 'Comment ça marche',
    title: 'Décrivez votre exploitation, on vous dit ce qui est finançable.',
    description:
      "Vous nous dites ce que vous avez comme bâtiments et équipements. On identifie les primes disponibles, on vérifie les aides de votre Région, on monte le dossier. Si vous passez par notre réseau d'artisans, la prime est déduite du devis.",
  },
  tools: {
    eyebrow: 'Vos outils',
    title: 'Des outils simples pour votre exploitation.',
    description:
      "Diagnostic agricole, simulateur d'aides, comparateur d'offres d'énergie pour les tarifs professionnels.",
  },
  caseStudy: {
    eyebrow: 'Un exemple concret',
    title: 'Une ferme laitière qui a cumulé primes énergie et PCAE, zéro avance.',
  },
  faq: {
    eyebrow: 'FAQ',
    title: 'Ce que vous voulez savoir.',
    items: [
      {
        q: "Mes bâtiments d'élevage sont-ils éligibles ?",
        a: "Oui, si les travaux améliorent l'efficacité énergétique : isolation, chauffage, éclairage, régulation. Plusieurs primes couvrent les bâtiments agricoles. On vérifie tout lors du diagnostic.",
      },
      {
        q: 'Je peux cumuler les primes et le PCAE ?',
        a: 'Oui, dans la plupart des cas. Ce sont deux dispositifs distincts. Notre simulateur vous donne les montants prévisionnels.',
      },
      {
        q: "MaPrimeRénov' s'applique à mes bâtiments agricoles ?",
        a: "Non, c'est pour les logements uniquement. Vos bâtiments d'exploitation passent par les primes énergie agricoles, le PCAE et FranceAgriMer.",
      },
      {
        q: 'Comment ça se passe pour le versement ?',
        a: 'Les primes sont déduites de votre devis si vous passez par notre réseau, ou versées après travaux. Le PCAE est versé par la Région. On vous explique tout au diagnostic.',
      },
    ],
  },
  ctaFinal: {
    badge: 'Diagnostic gratuit, 60 secondes',
    title: 'Combien peut rapporter votre exploitation ?',
    description:
      'Décrivez vos bâtiments, on chiffre les primes et les aides régionales en temps réel. Sans inscription, sans engagement.',
    primary: {
      label: 'Lancer le diagnostic',
      href: '/agriculteurs/diagnostic-cee',
      iconName: 'scan-line',
    },
    secondary: { label: 'Voir des cas agricoles', href: '/cas-clients' },
    footnote: 'Aucune avance, aucune inscription',
  },
};

// ════════════════════════════════════════════════════════════════
// TERTIAIRE
// ════════════════════════════════════════════════════════════════

const TERTIAIRE: AudienceVoice = {
  id: 'tertiaire',
  tone: 'tertiary-compliance',
  hero: {
    eyebrow: 'Bureaux, commerces, hôtels — à partir de 1 000 m²',
    promise: 'Votre obligation légale, gérée sans stress.',
    headline: 'Votre obligation -40 %,',
    headlineAccent: 'prise en charge de A à Z.',
    subheadline:
      "Vous devez réduire la consommation de votre bâtiment de 40 % d'ici 2030. On calcule comment y arriver, on planifie les travaux, et on les finance avec les primes disponibles.",
    bullets: [
      'Déclaration annuelle incluse',
      'Trajectoire -40 %, -50 %, -60 %',
      'Chaque étape financée',
    ],
    primaryCta: {
      label: 'Auditer mon bâtiment',
      href: '/tertiaire/decret-tertiaire',
      iconName: 'gauge',
    },
    secondaryCta: {
      label: 'Prendre rendez-vous',
      href: '/contact?source=tertiaire',
      iconName: 'handshake',
    },
    microPromise: 'Diagnostic 3 min, livrable PDF, sans engagement',
  },
  proof: {
    eyebrow: "Ce qu'on prend en charge",
    title: "Vos obligations transformées en plan d'action.",
    points: [
      {
        iconName: 'gauge',
        label: 'Déclaration annuelle',
        sub: 'On déclare vos consommations chaque année et on trace la trajectoire de réduction exigée par la loi.',
      },
      {
        iconName: 'building-2',
        label: 'Gestion technique du bâtiment',
        sub: 'Obligation de pilotage intelligent pour les bâtiments de plus de 290 kW. On vous accompagne.',
      },
      {
        iconName: 'file-text',
        label: 'Financement des travaux',
        sub: 'Chaque opération (chauffage, isolation, éclairage, ventilation) est financée par une prime dédiée.',
      },
    ],
  },
  method: {
    eyebrow: 'Notre méthode',
    title: 'Un seul interlocuteur pour toute votre conformité.',
    description:
      "On récupère vos données de consommation, on calcule votre année de référence, on dépose la déclaration annuelle, on construit le plan d'actions et on finance chaque opération.",
  },
  tools: {
    eyebrow: 'Vos outils',
    title: 'Pilotez la conformité de votre patrimoine.',
    description:
      'Audit réglementaire, trajectoire visuelle, simulateur de gestion technique, catalogue des primes.',
  },
  caseStudy: {
    eyebrow: 'Un exemple concret',
    title: 'Un immeuble de 8 200 m², -47 % de consommation, 312 000 € de primes mobilisées.',
  },
  faq: {
    eyebrow: 'FAQ',
    title: 'Vos obligations, expliquées simplement.',
    items: [
      {
        q: 'Quels bâtiments sont concernés ?',
        a: 'Tout bâtiment tertiaire (bureaux, commerces, hôtels, santé, enseignement, sport) dont la surface atteint ou dépasse 1 000 m².',
      },
      {
        q: 'Comment choisir mon année de référence ?',
        a: "On choisit ensemble l'année la plus représentative entre 2010 et 2019. On vous aide à constituer le dossier.",
      },
      {
        q: 'Quelles sanctions si je ne fais rien ?',
        a: "D'abord une mise en demeure publique, puis une amende pouvant aller jusqu'à 7 500 € par bâtiment. Les contrôles se sont intensifiés.",
      },
      {
        q: 'Les travaux peuvent être financés ?',
        a: 'Oui. Chaque opération est couverte par une prime. On active aussi les dispositifs de suramortissement et les prêts à taux préférentiels.',
      },
    ],
  },
  ctaFinal: {
    badge: 'Audit offert, sans engagement',
    title: 'Sécurisez votre conformité 2030.',
    description:
      'On chiffre votre plan de réduction, on finance vos travaux avec les primes et on sécurise votre déclaration annuelle. Confidentialité garantie.',
    primary: {
      label: 'Auditer mon bâtiment',
      href: '/tertiaire/decret-tertiaire',
      iconName: 'gauge',
    },
    secondary: { label: 'Prendre rendez-vous', href: '/contact?source=tertiaire' },
    footnote: 'Confidentialité, livrables structurés',
  },
};

// ════════════════════════════════════════════════════════════════
// INDUSTRIE
// ════════════════════════════════════════════════════════════════

const INDUSTRIE: AudienceVoice = {
  id: 'industrie',
  tone: 'industrial-engineering',
  hero: {
    eyebrow: 'Industriels, sites de production',
    promise: 'Audit obligatoire réalisé, primes identifiées.',
    headline: 'Vos consommations industrielles,',
    headlineAccent: 'auditées et optimisées.',
    subheadline:
      'Audit énergétique réglementaire, primes sur vos équipements, subventions cumulables. On identifie ce qui coûte cher et ce qui peut être financé.',
    bullets: [
      'Audit obligatoire conforme',
      'Primes sur vos équipements',
      'Aides publiques cumulées',
    ],
    primaryCta: {
      label: 'Diagnostic CEE industriel',
      href: '/industrie/diagnostic-cee',
      iconName: 'scan-line',
    },
    secondaryCta: {
      label: 'Prendre rendez-vous',
      href: '/contact?source=industrie',
      iconName: 'handshake',
    },
    microPromise: 'Diagnostic 5 min, livrable structuré, sans engagement',
  },
  proof: {
    eyebrow: 'Vos leviers',
    title: 'Vos obligations transformées en économies.',
    points: [
      {
        iconName: 'file-search',
        label: 'Audit énergétique obligatoire',
        sub: "Dès 250 salariés ou 50 M€ de chiffre d'affaires, renouvelé tous les 4 ans. On le réalise pour vous.",
      },
      {
        iconName: 'cog',
        label: 'Primes sur vos équipements',
        sub: 'Variateurs, moteurs haute efficacité, récupération de chaleur, air comprimé, isolation des réseaux.',
      },
      {
        iconName: 'flame',
        label: 'Fonds chaleur et décarbonation',
        sub: 'Aides pour la chaleur renouvelable, la décarbonation industrielle et les prêts à taux préférentiels.',
      },
    ],
  },
  method: {
    eyebrow: 'Notre méthode',
    title: 'Audit terrain, priorités, financement.',
    description:
      "Audit par un bureau d'études thermique partenaire, modélisation des consommations par poste, classement par retour sur investissement, dossier de financement complet.",
  },
  tools: {
    eyebrow: 'Vos outils',
    title: 'Pilotez votre performance énergétique.',
    description:
      "Diagnostic industriel, catalogue des fiches techniques, simulateur de subventions, plan d'actions.",
  },
  caseStudy: {
    eyebrow: 'Un exemple concret',
    title: 'Un site agroalimentaire : -22 % sur les utilités, 540 000 € de primes mobilisées.',
  },
  faq: {
    eyebrow: 'FAQ',
    title: 'Vos obligations et vos leviers.',
    items: [
      {
        q: "Qui est concerné par l'audit obligatoire ?",
        a: "Toute entreprise de plus de 250 salariés ou 50 M€ de chiffre d'affaires ou 43 M€ de bilan. Audit tous les 4 ans.",
      },
      {
        q: "Une certification ISO 50001 dispense-t-elle de l'audit ?",
        a: 'Oui, si elle couvre au moins 80 % de votre périmètre énergétique. Nous accompagnons les deux démarches.',
      },
      {
        q: 'Quels équipements sont éligibles aux primes ?',
        a: "Variateurs, moteurs, air comprimé, récupération de chaleur, isolation, éclairage. On liste ce qui s'applique à votre site.",
      },
      {
        q: 'Le Fonds chaleur est-il cumulable avec les primes ?',
        a: 'Oui, dans la limite des plafonds européens. On instruit les deux en parallèle.',
      },
    ],
  },
  ctaFinal: {
    badge: 'Diagnostic industriel, sans engagement',
    title: "Chiffrez le potentiel d'économies de votre site.",
    description:
      'Diagnostic en 5 minutes sur votre SIRET et votre activité. Identification des opérations éligibles, estimation des primes, livrable PDF.',
    primary: {
      label: 'Diagnostic industriel',
      href: '/industrie/diagnostic-cee',
      iconName: 'scan-line',
    },
    secondary: { label: 'Prendre rendez-vous', href: '/contact?source=industrie' },
    footnote: "Bureau d'études partenaire, confidentialité",
  },
};

// ════════════════════════════════════════════════════════════════
// COPROPRIÉTÉS — aides cumulables
// ════════════════════════════════════════════════════════════════

export type CoproAide = {
  label: string;
  sub: string;
  color: 'brand' | 'accent' | 'lavender' | 'sage';
};

export const COPROPRIETES_AIDES: CoproAide[] = [
  {
    label: "MaPrimeRénov' Copro",
    sub: "Jusqu'à 30 % du coût des travaux pour le syndicat",
    color: 'brand',
  },
  {
    label: 'Primes énergie collectives',
    sub: "Primes pour l'isolation et le chauffage collectif",
    color: 'accent',
  },
  {
    label: 'Éco-PTZ collectif',
    sub: "0 % d'intérêts, jusqu'à 50 000 € par lot",
    color: 'lavender',
  },
  {
    label: 'Aides locales',
    sub: 'Fonds régionaux, métropoles, aides Anah',
    color: 'sage',
  },
];

// ════════════════════════════════════════════════════════════════
// EXPORT INDEX
// ════════════════════════════════════════════════════════════════

export const AUDIENCE_VOICE: Record<AudienceId, AudienceVoice> = {
  particuliers: PARTICULIERS,
  professionnels: PROFESSIONNELS,
  entreprises: ENTREPRISES,
  tertiaire: TERTIAIRE,
  industrie: INDUSTRIE,
  collectivites: COLLECTIVITES,
  coproprietes: COPROPRIETES,
  artisans: ARTISANS,
  agriculteurs: AGRICULTEURS,
};

export function getAudienceVoice(id: AudienceId): AudienceVoice {
  return AUDIENCE_VOICE[id];
}
